import type { HTMLAttributes } from 'react';

import type { AplusLayoutDefaultProps } from '../../Layouts/types';
import type { APlusItem } from '../../types';

import { Check } from '../Check/Check';
import { Icon } from '../Icon/Icon';
import { Name } from '../Name/Name';
import { Price } from '../Price/Price.client';
import { Info } from '../Info/Info.client';
import { ServiceCount } from '../ServiceCount/ServiceCount.client';

type CenterInFirstLineProps = Pick<HTMLAttributes<HTMLDivElement>, 'className'> & {
  children: React.ReactNode;
};

const CenterInFirstLine = ({ children, className }: CenterInFirstLineProps) => (
  <div className={`flex h-4 items-center${className ? ` ${className}` : ''}`}>{children}</div>
);

type ItemProps = Omit<APlusItem, 'serviceLayerId' | 'clusterLabel' | 'preselected'> & {
  id: string;
  selected: boolean;
  value?: number;
  onInfoClickHandler: () => void;
  onItemChangeHandler: (uuid: string) => void;
  onCountChangeHandler?: (uuid: string, count: number) => void;
  /** rendered right after the price, e.g. the call-center price pencil */
  priceActionSlot?: React.ReactNode;
  /** rendered as a line of its own below the row, right-aligned under the price */
  priceDetailsSlot?: React.ReactNode;
} & Pick<AplusLayoutDefaultProps, 'currency' | 'quantity'>;

/**
 * atom for Aplus - renders an APlus item row
 */
export const Item = ({
  uuid,
  mandatory,
  serviceIcon,
  name,
  price,
  free,
  currency,
  quantity,
  id,
  selected,
  value,
  serviceCount,
  onInfoClickHandler,
  onItemChangeHandler,
  onCountChangeHandler,
  priceActionSlot,
  priceDetailsSlot,
}: ItemProps) => {
  const handleChange = () => {
    onItemChangeHandler(uuid);
  };

  return (
    <div className="text-size-body-sm col-span-5 grid grid-cols-subgrid gap-x-1.5">
      <CenterInFirstLine>
        <Check mandatory={mandatory} id={id} selected={selected} onChange={handleChange} />
      </CenterInFirstLine>
      <div>
        <Icon src={serviceIcon} />
      </div>
      <div className="flex justify-stretch">
        <div className="flex flex-col justify-center">
          <Name mandatory={mandatory} name={name || ''} id={id} />
          {selected && serviceCount?.current && onCountChangeHandler && (
            <ServiceCount
              uuid={uuid}
              count={value ?? serviceCount.current}
              maximum={serviceCount.maximum}
              onCountChangeHandler={onCountChangeHandler}
            />
          )}
        </div>
      </div>
      <CenterInFirstLine className="justify-end">
        <Price
          price={price}
          currency={currency}
          free={free}
          mandatory={mandatory}
          quantity={quantity}
        />
        {priceActionSlot}
      </CenterInFirstLine>
      <CenterInFirstLine>
        <Info type="icon" onClick={onInfoClickHandler} trackingData={{ uuid, name }} />
      </CenterInFirstLine>
      {priceDetailsSlot && (
        <div className="col-span-3 col-start-3 mt-1 flex flex-col items-end gap-1">
          {priceDetailsSlot}
        </div>
      )}
    </div>
  );
};
